import { useState } from "react";
import { Box, Flex, Text } from "rebass";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";

import { IUserProfile } from "MVVM/@types";
import { UserBio } from "../Header/Components/UserBio";
import { USER_UPDATE, GET_USER_PROFILE } from "MVVM/GraphQL/user";
import { GamesPillSelector } from "MVVM/Components/GamesPillSelector";

interface EditProfileContentProps {
  onClose: () => void;
  user: IUserProfile;
}

export const EditProfileContent = ({
  onClose,
  user,
}: EditProfileContentProps) => {
  const [username, setUsername] = useState(user?.username || "");
  const [selectedPlayedGames, setSelectedPlayedGames] = useState<string[]>(
    (user?.playedGames as unknown as string[]) || [],
  );

  const history = useHistory();

  const [updateUser, { loading: updateLoading }] = useMutation(USER_UPDATE, {
    onCompleted: (data) => {
      if (!data.userUpdate.errors?.length) {
        onClose();
      }
    },
    refetchQueries: [
      {
        query: GET_USER_PROFILE,
        variables: { username: user?.username || user._id },
      },
    ],
  });

  const hasChanges = () => {
    const originalPlayedGames =
      (user?.playedGames as unknown as string[]) || [];
    const playedGamesChanged =
      selectedPlayedGames.length !== originalPlayedGames.length ||
      selectedPlayedGames.some((game) => !originalPlayedGames.includes(game)) ||
      originalPlayedGames.some((game) => !selectedPlayedGames.includes(game));

    const usernameChanged = username !== (user?.username || "");

    return playedGamesChanged || usernameChanged;
  };
  const handleSave = async () => {
    if (hasChanges()) {
      const input: { playedGames?: string[]; username?: string } = {};

      const originalPlayedGames =
        (user?.playedGames as unknown as string[]) || [];
      const playedGamesChanged =
        selectedPlayedGames.length !== originalPlayedGames.length ||
        selectedPlayedGames.some(
          (game) => !originalPlayedGames.includes(game),
        ) ||
        originalPlayedGames.some((game) => !selectedPlayedGames.includes(game));

      if (playedGamesChanged) {
        input.playedGames = selectedPlayedGames;
      }

      if (username !== (user?.username || "")) {
        input.username = username;
      }

      await updateUser({
        variables: { input },
      });
    }
  };

  const handleAvatarButtonClick = () => {
    history.push("/accountsettings");
  };

  const handleGameToggle = (gameKey: string) => {
    setSelectedPlayedGames((prev) => {
      if (prev.includes(gameKey)) {
        return prev.filter((key) => key !== gameKey);
      } else {
        return [...prev, gameKey];
      }
    });
  };

  return (
    <ModalWrapper>
      <ModalHeader>
        <HeaderTitle>Edit Profile</HeaderTitle>
      </ModalHeader>

      <ModalContent>
        <Section>
          <AvatarContainer>
            <Avatar
              src={user?.avatarUrl || "/default-avatar.jpg"}
              alt="Avatar"
            />
            <Flex flexDirection={"column"}>
              <SectionLabel mb="2">Avatar</SectionLabel>
              <ReplacePhotoButton onClick={handleAvatarButtonClick}>
                Replace Photo
              </ReplacePhotoButton>
            </Flex>
          </AvatarContainer>
        </Section>

        {/* <Section>
          <SectionLabel mb="2">Banner Image</SectionLabel>
          <Flex flexDirection={"column"} justifyContent={"start"}>
            <BannerImage
              src={user?.coverImageUrl || "/default-banner.jpg"}
              alt="Banner"
            />
            <ReplacePhotoButton disabled={true}>
              Replace Photo
            </ReplacePhotoButton>
          </Flex>
        </Section> */}

        <Section>
          <SectionLabel mb="2">Username</SectionLabel>
          <StyledInput
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username"
            disabled={!!user?.username}
          />
        </Section>

        {/* <Section>
          <SectionLabel mb="2">Country</SectionLabel>
          <StyledSelect disabled={true}>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
          </StyledSelect>
        </Section> */}

        <Section>
          <SectionLabel mb="2">Bio</SectionLabel>

          <UserBio user={user} />
        </Section>

        <Section>
          <SectionLabel mb="2">Played Games</SectionLabel>
          <GamesPillSelector
            selectedGames={selectedPlayedGames}
            onGameToggle={handleGameToggle}
          />
        </Section>

        <ActionButtons>
          <CancelButton onClick={onClose} disabled={updateLoading}>
            Cancel
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={updateLoading || !hasChanges()}
          >
            {updateLoading ? "Saving..." : "Save"}
          </SaveButton>
        </ActionButtons>
      </ModalContent>
    </ModalWrapper>
  );
};

const ModalWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: 12px;
  max-width: 700px;
  width: 100%;

  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.navy50};
`;

const ModalHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
`;

const ModalContent = styled(Box)`
  padding: 24px;
`;

const HeaderTitle = styled(Text)`
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
`;

const Section = styled(Box)`
  padding-bottom: 12px;
`;

const SectionLabel = styled(Box)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
`;

const AvatarContainer = styled(Flex)`
  align-items: center;
  gap: 16px;
`;

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
`;

const ReplacePhotoButton = styled.button`
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 6px;
  border: none;
  color: white;
  cursor: pointer;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 10px;
  width: fit-content;

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.shade600};
    color: ${({ theme }) => theme.colors.shade400};
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background: ${({ theme }) => theme.colors.shade600};
    }
  }
`;

const StyledInput = styled.input`
  background: ${({ theme }) => theme.colors.navy400};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 6px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.shade100};
  font-family: Barlow;
  font-size: 14px;
  padding: 12px;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.shade400};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.shade600};
    color: ${({ theme }) => theme.colors.shade400};
    cursor: not-allowed;
    opacity: 0.6;

    &:focus {
      border-color: ${({ theme }) => theme.colors.navy50};
    }
  }
`;

const ActionButtons = styled(Flex)`
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const CancelButton = styled.button`
  background: ${({ theme }) => theme.colors.navy50};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: ${({ theme }) => theme.colors.navy50};
      color: ${({ theme }) => theme.colors.shade300};
    }
  }
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: ${({ theme }) => theme.colors.envy};
    }
  }
`;
